// src/templates/casestudy.tsx
import React, { useState } from "react"
import Layout from "../components/layout"
import Seo from "../components/seo"
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import * as allImages from "../utils/images"
import { style, style as styles } from "../pages/homepage"
import { FormikProvider, Field, useFormik } from "formik"
import axios from "axios"
import * as Yup from "yup"
import { API_URL } from "../utils/config"
import { InfoMessage, headers } from "../utils/utils"
import { Button as ReactBtn } from "react-bootstrap"
import casestudybg from "../images/case-study/casestudybg.png"
import downloadicon from "../images/newIcons/downloadicon.svg"

interface BusinessBenefit {
  title: string
  details: string
}

interface Implementation {
  title: string
  details: string
}

interface CaseStudyTemplateProps {
  pageContext: {
    id: string
    title: string
    introduction?: string
    metaTitle?: string
    metaDescription?: string
    metaKeywords?: string
    challenges: string
    solutions: string
    implementation?: Implementation[]
    businessBenefits?: BusinessBenefit[]
    takeaways: string
    image: string
    caseStudyTeaser: string
    dontMissOut: string
  }
}

const CaseStudyTemplate: React.FC<CaseStudyTemplateProps> = ({
  pageContext,
}) => {
  const {
    id,
    title,
    introduction,
    metaTitle,
    metaDescription,
    metaKeywords,
    image,
    challenges,
    solutions,
    implementation,
    businessBenefits,
    takeaways,
    caseStudyTeaser,
    dontMissOut,
  } = pageContext
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  // const isTablet = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = React.useState(false)
  const [startLoader, setStartLoader] = React.useState(false)

  const [infoMessage, setInfoMessage] = useState({} as InfoMessage)
  const titleColors = ["#D77A4B", "#AC9C30", "#1EAE3F"] // Your specified colors
  const colors = ["#D77A4B", "#AC9C30", "#1EAE3F"] // Your specified colors

  const SignupSchema = Yup.object().shape({
    name: Yup.string().required("*Required"),
    email: Yup.string().email("Invalid email").required("*Required")
    .test(
      "No gmail, yahoo, hotmail, proton and rediffmail",
      "Please enter a valid work email",
      value => {
        if (!value) return false
        const invalidDomains = ["gmail", "yahoo", "rediffmail", "hotmail", "proton"]
        return (
          !invalidDomains.includes(value?.split("@")[1]?.split(".")[0])
        )
      }
    ),
    comapanyName: Yup.string().required("*Required"),
  })

  const initialValues = {
    name: "",
    email: "",
    comapanyName: "",
    mobileNumber: "",
    caseStudyName: "",
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: SignupSchema,
    onSubmit: () => {
      submitData()
    },
  })

  const handleDownload = (res: any) => {
    const byteArray = Uint8Array.from(atob(res.data?.base64Content), char =>
      char.charCodeAt(0)
    )
    const blob = new Blob([byteArray], { type: "application/pdf" })

    try {
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `${title}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error("Error creating or triggering download:", error)
    }
  }

  const submitData = async () => {
    setStartLoader(true)
    try {
      const payload = {
        ...formik.values,
        caseStudyName: `${title}`,
        fileName: `${id}.pdf`,
      }
      await axios
        .post(`${API_URL}/download/casestudy`, payload, {
          headers,
        })
        .then((res: any) => {
          // disable loader
          if (res.data.status === 200) {
            setInfoMessage({ message: "", status: res.data.status })
            setOpen(true)
            formik.resetForm()
            // handleDownload(res.data?.base64Content);
            const byteArray = Uint8Array.from(
              atob(res.data?.base64Content),
              char => char.charCodeAt(0)
            )
            const blob = new Blob([byteArray], { type: "application/pdf" })

            // Use try-catch block to handle potential errors
            try {
              const link = document.createElement("a")
              link.href = window.URL.createObjectURL(blob)
              link.download = `${title}.pdf`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            } catch (error) {
              setStartLoader(false)
              console.error("Error creating or triggering download:", error)
            }
            setStartLoader(false)
          }
          setStartLoader(false)
        })
        .catch(error => {
          setStartLoader(false)
          setInfoMessage({
            message: error.response.data.message,
            status: error.response.data.status,
          })
          //TODO:: change alert to modal
          // alert("Something went wrong please try again later.");
        })
    } catch (err) {}
  }
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    setOpen(false)
  }

  let fileName = id
  let fileNameWithoutExtension = fileName.replace(".png", "")
  let camelCaseName: string = fileNameWithoutExtension.replace(
    /[-_](.)/g,
    (_, char) => char.toUpperCase()
  )

  return (
    <Layout showBlackNavbar={true}>
      <Seo
        title={metaTitle ? metaTitle : title}
        description={metaDescription ? metaDescription : caseStudyTeaser}
        keywords={metaKeywords}
        url="careers"
      />

      <section className="newheroSection">
        <Grid
          container
          alignItems="center"
          sx={{
            mt: isMobile ? 10 : 7.5,
            direction: isMobile ? "column" : "row",
          }}
        >
          <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
            <Box sx={{ position: "relative" }}>
              <Stack>
                <img
                  src={casestudybg}
                  className="casestudyBg1"
                  style={{ width: "100%", height: isMobile ? 740 : 840 }}
                />
              </Stack>
              <Stack
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <img src={allImages[camelCaseName]} />
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={9} lg={9}>
            <Grid container maxWidth="xl" justifyContent="center">
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Stack
                  // className="bannerHeading"
                  pl={isMobile ? 3 : 0}
                  pr={isMobile ? 3 : 2}
                  py={isMobile ? 6 : 0}
                >
                  <Typography
                    variant="h1"
                    fontSize={30}
                    fontWeight={700}
                    color="#256AA3"
                    mb={isMobile ? 3 : 4}
                  >
                    “{title}”
                  </Typography>
                  {
                    <Typography
                      variant="h2"
                      color="rgba(0, 0, 0, 0.70)"
                      lineHeight="28px"
                      fontSize={18}
                      fontWeight={500}
                      mb={3}
                    >
                      {introduction && introduction?.length ? (
                        <span style={{ fontWeight: 600 }}>{introduction}</span>
                      ) : (
                        <span>
                          Welcome to Invimatic – where creativity meets
                          technology! Our passion lies in crafting digital
                          solutions that drive success and elevate brand
                          experiences. With a focus on innovation and
                          collaboration, we're here to help businesses thrive in
                          the digital age.
                        </span>
                      )}
                    </Typography>
                  }
                  {caseStudyTeaser && (
                    <Typography
                      variant="h6"
                      color="rgba(0, 0, 0, 0.70)"
                      fontSize={18}
                      mb={3}
                      dangerouslySetInnerHTML={{ __html: caseStudyTeaser }}
                    ></Typography>
                  )}
                  <Typography
                    variant="h6"
                    color="rgba(0, 0, 0, 0.70)"
                    fontSize={18}
                  >
                    Don't miss out –{" "}
                    {dontMissOut ? (
                      dontMissOut
                    ) : (
                      <span style={{ fontWeight: "1000" }}>Download Now!</span>
                    )}
                  </Typography>
                </Stack>
              </Grid>
              {open && (
                <Snackbar
                  open={open}
                  autoHideDuration={2000}
                  onClose={handleClose}
                  message="PDF Downloaded succesfully"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    top: "150px !important",
                    ".MuiSnackbarContent-root": {
                      backgroundColor: "#fff",
                      color: "#000",
                      justifyContent: "center",
                      fontWeight: "700",
                      p: 1,
                    },
                    ".MuiSnackbarContent-message": {
                      p: 0,
                    },
                  }}
                />
              )}
              <Grid item xs={12} sm={12} md={5} lg={5} p={isMobile ? 3 : 0}>
                <Stack
                  sx={{
                    padding: "40px",
                    borderRadius: "10px",
                    border: "4px solid #256AA3",
                    backgroundColor: "#F6F9FF",
                    position: "relative",
                  }}
                >
                  <FormikProvider value={formik}>
                    <form
                      action="https://forms.hsforms.com/submissions/v3/integration/submit/46024254/04f51a20-899e-434d-b91a-ee723ca8b26d"
                      method="POST"
                      onSubmit={formik.handleSubmit}
                    >
                      <Grid container spacing={3.5}>
                        <Grid item xs={12}>
                          <Field name="name">
                            {({ field, meta }: any) => (
                              <>
                                <TextField
                                  sx={{
                                    borderRadius: "4px",
                                    background: "#FFF",

                                    ".MuiOutlinedInput-input": {
                                      padding: "14px",
                                    },
                                  }}
                                  name="name"
                                  id="name"
                                  variant="outlined"
                                  label="Full Name"
                                  fullWidth
                                  {...field}
                                  value={field.value}
                                />
                                {meta.touched && meta.error && (
                                  <Typography
                                    sx={style.error}
                                    variant="caption"
                                  >
                                    {meta.error}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Field>
                        </Grid>

                        <Grid item xs={12}>
                          <Field name="comapanyName">
                            {({ field, meta }: any) => (
                              <>
                                <TextField
                                  sx={{
                                    borderRadius: "4px",
                                    background: "#FFF",
                                    ".MuiOutlinedInput-input": {
                                      padding: "14px",
                                    },
                                  }}
                                  type="text"
                                  name="comapanyName"
                                  id="comapanyName"
                                  variant="outlined"
                                  label="Company"
                                  fullWidth
                                  {...field}
                                  value={field.value}
                                />
                                {meta.touched && meta.error && (
                                  <Typography
                                    sx={style.error}
                                    variant="caption"
                                  >
                                    {meta.error}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Field>
                        </Grid>

                        <Grid item xs={12}>
                          <Field name="email">
                            {({ field, meta }: any) => (
                              <>
                                <TextField
                                  sx={{
                                    borderRadius: "4px",
                                    background: "#FFF",
                                    ".MuiOutlinedInput-input": {
                                      padding: "14px",
                                    },
                                  }}
                                  type="email"
                                  name="email"
                                  id="email"
                                  variant="outlined"
                                  label="Work Email"
                                  fullWidth
                                  {...field}
                                  value={field.value}
                                />
                                {meta.touched && meta.error && (
                                  <Typography
                                    sx={style.error}
                                    variant="caption"
                                  >
                                    {meta.error}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12}>
                          <Field name="mobileNumber">
                            {({ field, meta }: any) => (
                              <>
                                <TextField
                                  sx={{
                                    borderRadius: "4px",
                                    background: "#FFF",
                                    ".MuiOutlinedInput-input": {
                                      padding: "14px",
                                    },
                                  }}
                                  type="text"
                                  name="mobileNumber"
                                  id="mobileNumber"
                                  variant="outlined"
                                  label="Contact Number"
                                  fullWidth
                                  {...field}
                                  value={field.value}
                                  onChange={e => {
                                    const validChars = /^[0-9+]+$/
                                    if (
                                      e.target.value === "" ||
                                      validChars.test(e.target.value)
                                    ) {
                                      field.onChange(e)
                                    }
                                  }}
                                />
                              </>
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            variant="h6"
                            color="#32403B"
                            fontSize={14}
                            fontWeight={400}
                          >
                            By submitting this form, you consent to Invimatic
                          </Typography>
                          <Typography
                            variant="h6"
                            fontSize={14}
                            fontWeight={700}
                          >
                            <Link
                              href="/privacy-policy/"
                              style={{
                                color: "#316084",
                                textDecoration: "underline",
                              }}
                            >
                              Privacy Policy
                            </Link>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                          <ReactBtn
                            type="submit"
                            className="btn btn-primary commonBtn"
                            style={{ padding: "12px 20px" }}
                          >
                            DOWNLOAD
                            {startLoader ? (
                              <Stack
                                sx={{
                                  alignItems: "center",
                                  ".MuiCircularProgress-indeterminate": {
                                    width: "20px !important",
                                    height: "20px !important",
                                  },
                                }}
                              >
                                <CircularProgress
                                  sx={{
                                    ".MuiCircularProgress-circle": {
                                      color: "#fff !important",
                                    },
                                  }}
                                />
                              </Stack>
                            ) : (
                              <img src={downloadicon} alt="Download" />
                            )}
                          </ReactBtn>
                        </Grid>
                        {infoMessage.message ? (
                          <Typography color="red" ml={4} mt={1}>
                            {infoMessage.message}
                          </Typography>
                        ) : (
                          ""
                        )}
                      </Grid>
                    </form>
                  </FormikProvider>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </section>
    </Layout>
  )
}

export default CaseStudyTemplate
